import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Actori (opt-in module, built for Genius Media) and the client industries.
// Mirrors backend/src/routes/actors.ts and routes/clientIndustries.ts.
// ---------------------------------------------------------------------------

export interface ClientIndustry {
  id: string;
  name: string;
  clientCount: number;
}

export interface ActorClientLink {
  clientId: string;
  clientName: string;
  clientActive: boolean;
  clientAvatarUrl: string | null;
  clientColor: string | null;
  industry: { id: string; name: string } | null;
  /** YYYY-MM-DD, the last shoot at this client. */
  usedAt: string;
  note: string | null;
  /** Set when the link was saved despite a competitor (who confirmed it, when). */
  conflictAcknowledgedAt: string | null;
  conflictAcknowledgedByName: string | null;
  /** The competitors named in the alert at that moment. */
  conflictWith: string[];
}

/** Two or more active clients of one industry: the "⚠ Conflict" badge. */
export interface ActorConflictGroup {
  industryId: string;
  industryName: string | null;
  clients: { clientId: string; clientName: string }[];
}

export interface Actor {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  avatarUrl: string | null;
  notes: string | null;
  isActive: boolean;
  companyId: string | null;
  clients: ActorClientLink[];
  conflicts: ActorConflictGroup[];
  createdAt: string;
  updatedAt: string;
}

export interface ActorConflict {
  clientId: string;
  clientName: string;
  industryId: string;
  industryName: string | null;
  usedAt: string;
}

export interface ActorConflictCheck {
  status: 'conflict' | 'ok' | 'unknown-industry';
  conflicts: ActorConflict[];
  client: { id: string; name: string; industry: { id: string; name: string } | null };
  /** „Atenție, acest actor a fost folosit la …, potențial conflict de interese.” */
  message: string | null;
}

export interface ClientActorRow {
  actor: Actor;
  link: ActorClientLink;
  /** Competitors of this client the actor also appeared at. */
  conflicts: ActorConflict[];
}

export interface ActorFilters {
  search?: string;
  industryId?: string;
  clientId?: string;
  companyId?: string | null;
  active?: 'true' | 'false' | 'all';
}

export interface ActorInput {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  avatarUrl?: string | null;
  notes?: string;
  companyId?: string | null;
  isActive?: boolean;
}

export interface LinkClientInput {
  clientId: string;
  usedAt?: string;
  note?: string;
  acknowledgeConflict?: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function headers(json = false): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function query(filters: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Industries ------------------------------------------------------------

export async function listClientIndustries(): Promise<ClientIndustry[]> {
  const res = await apiFetch<Envelope<ClientIndustry[]>>('/api/client-industries', {
    headers: headers(),
  });
  return res.data;
}

/** Adds an industry; an existing name (any case) comes back instead of a duplicate. */
export async function createClientIndustry(name: string): Promise<ClientIndustry> {
  const res = await apiFetch<Envelope<ClientIndustry>>('/api/client-industries', {
    method: 'POST',
    headers: headers(true),
    body: JSON.stringify({ name }),
  });
  return res.data;
}

// --- Actors ----------------------------------------------------------------

export async function listActors(filters: ActorFilters = {}): Promise<Actor[]> {
  const res = await apiFetch<Envelope<Actor[]>>(`/api/actors${query(filters)}`, {
    headers: headers(),
  });
  return res.data;
}

export async function getActor(id: string): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>(`/api/actors/${encodeURIComponent(id)}`, {
    headers: headers(),
  });
  return res.data;
}

export async function createActor(input: ActorInput): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>('/api/actors', {
    method: 'POST',
    headers: headers(true),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateActor(id: string, input: Partial<ActorInput>): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>(`/api/actors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: headers(true),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteActor(id: string): Promise<void> {
  await apiFetch(`/api/actors/${encodeURIComponent(id)}`, { method: 'DELETE', headers: headers() });
}

/** The check before adding a client: competitors the actor already appeared at. */
export async function checkActorConflicts(
  actorId: string,
  clientId: string,
): Promise<ActorConflictCheck> {
  const res = await apiFetch<Envelope<ActorConflictCheck>>(
    `/api/actors/${encodeURIComponent(actorId)}/conflicts${query({ clientId })}`,
    { headers: headers() },
  );
  return res.data;
}

export async function linkActorClient(actorId: string, input: LinkClientInput): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>(
    `/api/actors/${encodeURIComponent(actorId)}/clients`,
    { method: 'POST', headers: headers(true), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function updateActorClient(
  actorId: string,
  clientId: string,
  input: { usedAt?: string; note?: string },
): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>(
    `/api/actors/${encodeURIComponent(actorId)}/clients/${encodeURIComponent(clientId)}`,
    { method: 'PATCH', headers: headers(true), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function unlinkActorClient(actorId: string, clientId: string): Promise<Actor> {
  const res = await apiFetch<Envelope<Actor>>(
    `/api/actors/${encodeURIComponent(actorId)}/clients/${encodeURIComponent(clientId)}`,
    { method: 'DELETE', headers: headers() },
  );
  return res.data;
}

export async function listClientActors(clientId: string): Promise<ClientActorRow[]> {
  const res = await apiFetch<Envelope<ClientActorRow[]>>(
    `/api/clients/${encodeURIComponent(clientId)}/actors`,
    { headers: headers() },
  );
  return res.data;
}
